import React, { useState } from 'react';

// Define types for our component
interface JsonToMongoDBProps {}

// MongoDB query type options
type QueryType = 'find' | 'findOne' | 'update' | 'delete' | 'aggregate';

// MongoDB field operator types
type FieldOperator = 'equals' | 'gt' | 'gte' | 'lt' | 'lte' | 'ne' | 'in' | 'nin' | 'exists' | 'type' | 'regex';

// Theme options
type CodeTheme = 'light' | 'dark' | 'colorful' | 'minimal';

const JsonToMongoDB: React.FC<JsonToMongoDBProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the MongoDB query output
  const [mongoQuery, setMongoQuery] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for MongoDB query type
  const [queryType, setQueryType] = useState<QueryType>('find');
  // State for syntax highlighting theme
  const [theme, setTheme] = useState<CodeTheme>('light');
  // State to track if the conversion happened
  const [isQueryGenerated, setIsQueryGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for operator transformation
  const [useOperators, setUseOperators] = useState<boolean>(true);
  // State for variable name
  const [variableName, setVariableName] = useState<string>('db.collection');
  // State for including comments
  const [includeComments, setIncludeComments] = useState<boolean>(true);

  // Sample data for different query types
  const sampleData: Record<QueryType, any> = {
    find: {
      status: "active",
      age: { $gt: 25 },
      tags: { $in: ["mongodb", "database"] }
    },
    findOne: {
      email: "user@example.com",
      verified: true
    },
    update: {
      filter: { id: "12345" },
      update: { 
        $set: { status: "completed" },
        $inc: { count: 1 }
      }
    },
    delete: {
      status: "archived",
      createdAt: { $lt: "2023-01-01" }
    },
    aggregate: [
      { $match: { status: "active" } },
      { $group: { _id: "$category", total: { $sum: "$amount" } } },
      { $sort: { total: -1 } }
    ]
  };

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsQueryGenerated(false);
  };

  // Handle variable name change
  const handleVariableNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVariableName(e.target.value);
  };

  // Use example data based on the selected query type
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData[queryType], null, 2));
    setError('');
    setIsQueryGenerated(false);
    setMongoQuery('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setMongoQuery('');
    setError('');
    setQueryType('find');
    setTheme('light');
    setIsQueryGenerated(false);
    setVariableName('db.collection');
  };

  // Get field operator based on value and key
  const getFieldOperator = (value: any, key: string): [string, any] => {
    // Skip transforming _id fields by default
    if (key === '_id' && !useOperators) {
      return [key, value];
    }

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const operatorKeys = Object.keys(value);
      // Check if the object already has MongoDB operators
      if (operatorKeys.length > 0 && operatorKeys[0].startsWith('$')) {
        return [key, value];
      }
    }

    if (useOperators) {
      // Transform simple equality to $eq for consistency when useOperators is true
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return [key, { $eq: value }];
      }
    }

    return [key, value];
  };

  // Process objects recursively to convert to MongoDB format
  const processObjectForMongo = (obj: any): any => {
    if (obj === null) {
      return null;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => processObjectForMongo(item));
    }

    if (typeof obj === 'object') {
      const result: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        if (queryType === 'update' && (key === 'filter' || key === 'update')) {
          // Special handling for update objects
          result[key] = processObjectForMongo(value);
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Process nested objects
          const [processedKey, processedValue] = getFieldOperator(processObjectForMongo(value), key);
          result[processedKey] = processedValue;
        } else {
          const [processedKey, processedValue] = getFieldOperator(value, key);
          result[processedKey] = processedValue;
        }
      }
      return result;
    }

    return obj;
  };

  // Generate the MongoDB query string with proper formatting
  const generateMongoQueryString = (data: any): string => {
    let query = '';
    let comments = '';
    
    switch (queryType) {
      case 'find':
        if (includeComments) {
          comments = '// Find documents matching the specified criteria\n';
        }
        query = `${comments}${variableName}.find(${JSON.stringify(data, null, 2)})`;
        break;
      case 'findOne':
        if (includeComments) {
          comments = '// Find a single document matching the criteria\n';
        }
        query = `${comments}${variableName}.findOne(${JSON.stringify(data, null, 2)})`;
        break;
      case 'update':
        if (!data.filter || !data.update) {
          throw new Error('Update queries require both "filter" and "update" properties');
        }
        if (includeComments) {
          comments = '// Update documents matching the filter with the specified updates\n';
        }
        query = `${comments}${variableName}.updateMany(
  ${JSON.stringify(data.filter, null, 2)},
  ${JSON.stringify(data.update, null, 2)}
)`;
        break;
      case 'delete':
        if (includeComments) {
          comments = '// Delete documents matching the criteria\n';
        }
        query = `${comments}${variableName}.deleteMany(${JSON.stringify(data, null, 2)})`;
        break;
      case 'aggregate':
        if (!Array.isArray(data)) {
          throw new Error('Aggregate queries require an array of pipeline stages');
        }
        if (includeComments) {
          comments = '// Aggregate pipeline for data transformation and analysis\n';
        }
        query = `${comments}${variableName}.aggregate(${JSON.stringify(data, null, 2)})`;
        break;
      default:
        query = `${variableName}.find(${JSON.stringify(data, null, 2)})`;
    }
    
    return query;
  };

  // Apply syntax highlighting
  const highlightSyntax = (code: string): string => {
    // Define regex patterns for different elements
    const patterns = {
      string: /"(?:\\.|[^"\\])*"/g,
      number: /\b\d+\b/g,
      boolean: /\b(?:true|false|null)\b/g,
      operator: /\$\w+/g,
      comment: /\/\/.*$/gm,
      function: /\.\w+(?=\()/g,
      variable: new RegExp(`\\b${variableName.replace(/\./g, '\\.')}\\b`, 'g'),
      bracket: /[[\]{}()]/g,
    };
    
    // Apply CSS classes based on the theme
    let highlighted = code
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Apply highlighting for each pattern
    highlighted = highlighted
      .replace(patterns.string, match => `<span class="mongo-string">${match}</span>`)
      .replace(patterns.number, match => `<span class="mongo-number">${match}</span>`)
      .replace(patterns.boolean, match => `<span class="mongo-boolean">${match}</span>`)
      .replace(patterns.operator, match => `<span class="mongo-operator">${match}</span>`)
      .replace(patterns.comment, match => `<span class="mongo-comment">${match}</span>`)
      .replace(patterns.function, match => `<span class="mongo-function">${match}</span>`)
      .replace(patterns.variable, match => `<span class="mongo-variable">${match}</span>`)
      .replace(patterns.bracket, match => `<span class="mongo-bracket">${match}</span>`);
    
    return highlighted;
  };

  // Get CSS for the selected theme
  const getThemeStyles = (): string => {
    switch (theme) {
      case 'dark':
        return `
          .mongo-preview { background-color: #1a1a1a; color: #f5f5f5; padding: 16px; border-radius: 8px; font-family: 'Courier New', monospace; white-space: pre; overflow-x: auto; }
          .mongo-string { color: #a3be8c; }
          .mongo-number { color: #b48ead; }
          .mongo-boolean { color: #ebcb8b; }
          .mongo-operator { color: #88c0d0; font-weight: bold; }
          .mongo-comment { color: #4c566a; font-style: italic; }
          .mongo-function { color: #81a1c1; font-weight: bold; }
          .mongo-variable { color: #d08770; }
          .mongo-bracket { color: #8fbcbb; }
        `;
      case 'colorful':
        return `
          .mongo-preview { background-color: #f8f9fa; color: #333; padding: 16px; border-radius: 8px; font-family: 'Courier New', monospace; white-space: pre; overflow-x: auto; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
          .mongo-string { color: #28a745; }
          .mongo-number { color: #fd7e14; }
          .mongo-boolean { color: #007bff; }
          .mongo-operator { color: #e83e8c; font-weight: bold; }
          .mongo-comment { color: #6c757d; font-style: italic; }
          .mongo-function { color: #6f42c1; font-weight: bold; }
          .mongo-variable { color: #dc3545; }
          .mongo-bracket { color: #20c997; }
        `;
      case 'minimal':
        return `
          .mongo-preview { background-color: #ffffff; color: #333; padding: 16px; border-radius: 8px; font-family: 'Courier New', monospace; white-space: pre; overflow-x: auto; border: 1px solid #eee; }
          .mongo-string { font-weight: normal; }
          .mongo-number { font-weight: normal; }
          .mongo-boolean { font-weight: normal; }
          .mongo-operator { font-weight: bold; }
          .mongo-comment { font-style: italic; color: #666; }
          .mongo-function { font-weight: bold; }
          .mongo-variable { font-weight: bold; }
          .mongo-bracket { font-weight: normal; }
        `;
      default: // light theme
        return `
          .mongo-preview { background-color: #f8f9fa; color: #333; padding: 16px; border-radius: 8px; font-family: 'Courier New', monospace; white-space: pre; overflow-x: auto; box-shadow: 0 2px 5px rgba(0,0,0,0.1); }
          .mongo-string { color: #008000; }
          .mongo-number { color: #d63384; }
          .mongo-boolean { color: #9c27b0; }
          .mongo-operator { color: #0056b3; font-weight: bold; }
          .mongo-comment { color: #6c757d; font-style: italic; }
          .mongo-function { color: #495057; font-weight: bold; }
          .mongo-variable { color: #e63946; }
          .mongo-bracket { color: #495057; }
        `;
    }
  };

  // Convert JSON to MongoDB query
  const convertJsonToMongoQuery = () => {
    setError('');
    setIsLoading(true);
    setIsQueryGenerated(false);
    setMongoQuery('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      // Process JSON data based on query type
      const processedData = processObjectForMongo(jsonData);
      
      // Generate MongoDB query string
      const query = generateMongoQueryString(processedData);
      
      setMongoQuery(query);
      setIsQueryGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating MongoDB query: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy query to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(mongoQuery)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to MongoDB Query Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to MongoDB query syntax. This tool helps you transform your JSON objects into MongoDB compatible query commands.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"status": "active", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="queryType" className="block text-sm font-medium text-gray-700 mb-1">
                MongoDB Query Type
              </label>
              <select
                id="queryType"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={queryType}
                onChange={(e) => {
                  setQueryType(e.target.value as QueryType);
                  setIsQueryGenerated(false);
                }}
              >
                <option value="find">find() - Query multiple documents</option>
                <option value="findOne">findOne() - Query a single document</option>
                <option value="update">update() - Update documents</option>
                <option value="delete">delete() - Delete documents</option>
                <option value="aggregate">aggregate() - Aggregation pipeline</option>
              </select>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="variableName" className="block text-sm font-medium text-gray-700 mb-1">
                    Collection Variable Name
                  </label>
                  <input
                    type="text"
                    id="variableName"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={variableName}
                    onChange={handleVariableNameChange}
                    placeholder="e.g., db.users, db.products, etc."
                  />
                </div>
                
                <div>
                  <label htmlFor="theme" className="block text-sm font-medium text-gray-700 mb-1">
                    Syntax Highlighting Theme
                  </label>
                  <select
                    id="theme"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value as CodeTheme)}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="colorful">Colorful</option>
                    <option value="minimal">Minimal</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useOperators"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={useOperators}
                    onChange={(e) => setUseOperators(e.target.checked)}
                  />
                  <label htmlFor="useOperators" className="ml-2 block text-sm text-gray-700">
                    Use explicit operators (transforms "field: value" to "field: ")
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeComments"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={includeComments}
                    onChange={(e) => setIncludeComments(e.target.checked)}
                  />
                  <label htmlFor="includeComments" className="ml-2 block text-sm text-gray-700">
                    Include explanatory comments
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToMongoQuery}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to MongoDB Query'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              MongoDB Query Output
            </label>
            
            {isQueryGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Query'}
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isQueryGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  <p>
                    MongoDB query will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isQueryGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="mb-4">
                  <h3 className="font-medium text-gray-800 mb-2">Preview:</h3>
                  <div
                    className="mongo-preview"
                    dangerouslySetInnerHTML={{ __html: highlightSyntax(mongoQuery) }}
                  />
                </div>
                
                <div className="flex-grow flex flex-col">
                  <label htmlFor="mongoOutput" className="text-sm font-medium text-gray-700 mb-1">
                    MongoDB Query Code:
                  </label>
                  <textarea
                    id="mongoOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={mongoQuery}
                    readOnly
                  />
                </div>
                
                <style dangerouslySetInnerHTML={{ __html: getThemeStyles() }} />
              </div>
            )}
          </div>
          
          {isQueryGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">MongoDB Query Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The query is ready to use in your MongoDB application or shell.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to MongoDB Query Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON data into MongoDB query syntax. It intelligently converts regular JSON objects
              into MongoDB query format, applying operators and formatting according to your selected query type.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Query Types Explained</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>find()</strong> - For querying multiple documents that match the criteria</li>
              <li><strong>findOne()</strong> - For retrieving a single document matching the criteria</li>
              <li><strong>update()</strong> - For modifying documents (requires filter and update objects)</li>
              <li><strong>delete()</strong> - For removing documents that match the criteria</li>
              <li><strong>aggregate()</strong> - For complex data processing using an aggregation pipeline</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">MongoDB Operator Examples</h3>
            <div className="bg-gray-50 p-3 rounded-md font-mono text-sm">
              <p><strong>$eq</strong>: Matches values that are equal to a specified value</p>
              <p><strong>$gt</strong>: Matches values that are greater than a specified value</p>
              <p><strong>$lt</strong>: Matches values that are less than a specified value</p>
              <p><strong>$in</strong>: Matches any of the values specified in an array</p>
              <p><strong>$exists</strong>: Matches documents that have the specified field</p>
                <p><strong>$regex</strong>: Matches documents that contain a specified string pattern</p>
            </div>
            </div>
            <div>
                <h3 className="font-medium text-gray-800 mb-2">Tips for Using This Converter</h3>
                <ul className="list-disc pl-5 text-gray-600">
                    <li>Ensure your JSON data is valid before conversion.</li>
                    <li>Use the "Format JSON" button to beautify your JSON input.</li>
                    <li>Explore advanced options for customizing the output format.</li>
                    <li>Copy the generated query directly to your MongoDB shell or application.</li>
                </ul>
            </div>
        </div>
        </div>
    </div>
    );
};
export default JsonToMongoDB;